import { useEffect, useRef } from "react";
import { Capacitor } from "@capacitor/core";
import {
  fetchNotificationBadges,
  fetchUpcomingReminders,
} from "../utils/notificationsApi";

function strIdToInt(s) {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h << 5) - h + s.charCodeAt(i);
  return Math.abs(h | 0) % 2000000000 + 1;
}

/**
 * App native : notifications locales (nouveau doc / RDV / annulation + rappels J-1 et ~2h avant).
 */
export default function NativeNotificationBridge() {
  const lastSnap = useRef({ documents: 0, rdv: 0 });
  const aliveRef = useRef(true);
  const timersRef = useRef({ t1: null, t2: null });

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;
    aliveRef.current = true;

    (async () => {
      let LN = null;
      try {
        const mod = await import("@capacitor/local-notifications");
        if (!aliveRef.current) return;
        LN = mod.LocalNotifications;
        const perm = await LN.requestPermissions();
        if (perm.display !== "granted") return;
      } catch {
        return;
      }

      const maybeNotifyDelta = async (next) => {
        const prev = lastSnap.current;
        const docUp = next.documents > prev.documents;
        const rdvUp = next.rdv > prev.rdv;
        lastSnap.current = { ...next };
        if (!docUp && !rdvUp) return;
        let body = "";
        if (docUp && rdvUp) body = "Nouveau document et mise à jour de rendez-vous.";
        else if (docUp) body = "Nouveau document reçu.";
        else body = "Nouveau rendez-vous ou annulation à consulter.";
        try {
          await LN.schedule({
            notifications: [
              {
                title: "ProMeetService",
                body,
                id: Math.floor(Math.random() * 2 ** 30),
                schedule: { at: new Date(Date.now() + 1200) },
              },
            ],
          });
        } catch {
          /* ignore */
        }
      };

      const pollBadges = async () => {
        if (!aliveRef.current) return;
        const next = await fetchNotificationBadges();
        await maybeNotifyDelta(next);
      };

      const pollReminders = async () => {
        if (!aliveRef.current || !LN) return;
        const rdvs = await fetchUpcomingReminders();
        const now = Date.now();
        for (const r of rdvs) {
          const start = r.start ? new Date(r.start).getTime() : 0;
          if (!start) continue;
          const ms = start - now;
          const label = r.activity || r.societeName || "Rendez-vous";
          const dayKey = `pmsRem1d-${r._id}`;
          const soonKey = `pmsRemSoon-${r._id}`;

          if (ms > 23 * 3600 * 1000 && ms < 25 * 3600 * 1000) {
            if (sessionStorage.getItem(dayKey)) continue;
            sessionStorage.setItem(dayKey, "1");
            try {
              await LN.schedule({
                notifications: [
                  {
                    title: "Rappel rendez-vous",
                    body: `Demain : ${label}`,
                    id: strIdToInt(`${r._id}-d1`),
                    schedule: { at: new Date(Date.now() + 2000) },
                  },
                ],
              });
            } catch {
              /* ignore */
            }
          }

          if (ms > 1.5 * 3600 * 1000 && ms < 2.3 * 3600 * 1000) {
            if (sessionStorage.getItem(soonKey)) continue;
            sessionStorage.setItem(soonKey, "1");
            try {
              await LN.schedule({
                notifications: [
                  {
                    title: "Rappel rendez-vous",
                    body: `Dans environ 2 h : ${label}`,
                    id: strIdToInt(`${r._id}-soon`),
                    schedule: { at: new Date(Date.now() + 2000) },
                  },
                ],
              });
            } catch {
              /* ignore */
            }
          }

          const startDay = new Date(r.start);
          const d0 = new Date();
          const today =
            startDay.getDate() === d0.getDate() &&
            startDay.getMonth() === d0.getMonth() &&
            startDay.getFullYear() === d0.getFullYear();
          const sameDayKey = `pmsRemToday-${r._id}`;
          if (today && ms > 45 * 60 * 1000 && ms < 3 * 3600 * 1000) {
            if (sessionStorage.getItem(sameDayKey)) continue;
            sessionStorage.setItem(sameDayKey, "1");
            try {
              await LN.schedule({
                notifications: [
                  {
                    title: "Rendez-vous aujourd’hui",
                    body: `N’oubliez pas : ${label}`,
                    id: strIdToInt(`${r._id}-today`),
                    schedule: { at: new Date(Date.now() + 2000) },
                  },
                ],
              });
            } catch {
              /* ignore */
            }
          }
        }
      };

      await pollBadges();
      await pollReminders();

      timersRef.current.t1 = window.setInterval(pollBadges, 75_000);
      timersRef.current.t2 = window.setInterval(pollReminders, 20 * 60 * 1000);
    })();

    return () => {
      aliveRef.current = false;
      if (timersRef.current.t1) window.clearInterval(timersRef.current.t1);
      if (timersRef.current.t2) window.clearInterval(timersRef.current.t2);
      timersRef.current.t1 = null;
      timersRef.current.t2 = null;
    };
  }, []);

  return null;
}
